import React from 'react';
import Popover from '@material-ui/core/Popover';
import { EMPHASIS_STAGE_KEY, NO_ANIMATION_KEY, REVERSE_ICON_LIST } from 'js/config/consts';
import { animationTypeHasNoDuration } from 'js/shared/helpers/animationTypeHasNoDuration';
import { AnimationTypeOption, ElementChoiceButtonProps, ElementMenuProps } from 'js/types';
import classNames from 'classnames';

import EditPanelNumberInput from '../EditPanelNumberInput';

import AnimationTypeSelectorButtons, {
  AnimationTypeSelectorButtonGroup,
  AnimationTypeSelectorButtonGroupText
} from './AnimationTypeSelectorButtons';
import { MenuConfig, useAnimationStageContext } from './AnimationStageContext';
import AnimationOptionMenu from './AnimationOptionMenu';
import OptionMenuButton, { OptionMenuButtonIconAndText } from './OptionMenuButton';

import './AnimationStageConfigurationOptions.css';

export const AnimationStagePanelOptionsGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="AnimationStageConfigurationOptions__optionsGrid">{children}</div>
);

interface AnimationStageConfigurationOptionsProps {
  panelId: string;
  variant?: 'dark' | 'light';
  size?: 'small' | 'large';
  disabled?: boolean;
}

const AnimationStageConfigurationOptions: React.FC<AnimationStageConfigurationOptionsProps> = ({
  panelId,
  variant = 'dark',
  size = 'small',
  disabled = false
}) => {
  const {
    animationType,
    animationTypeOptions,
    animationTypeTitle,
    closeMenu,
    duration,
    Icon,
    loops,
    menuOpen,
    onAnimationTypeChange,
    onDurationChange,
    onLoopsChange,
    secondaryMenu,
    setMenuOpen,
    showDuration,
    stageKey,
    tertiaryMenu
  } = useAnimationStageContext();

  if (!stageKey) {
    return null;
  }

  const popoverClassName = classNames(
    'AnimationStageConfigurationOptions__menu',
    `AnimationStageConfigurationOptions__menu--${variant}`
  );

  const SecondaryChoiceButton = secondaryMenu?.ChoiceButton;
  const TertiaryChoiceButton = tertiaryMenu?.ChoiceButton;

  const showLoops = !!onLoopsChange;

  const handleButtonClick = (menuOpenConfig: MenuConfig) => {
    setMenuOpen(menuOpenConfig);
  };

  const handleMenuItemClick = (option: AnimationTypeOption<ElementChoiceButtonProps, ElementMenuProps>) => {
    onAnimationTypeChange(option.type);
  };

  return (
    <>
      <div className="AnimationStageConfigurationOptions__fieldset">
        <AnimationTypeSelectorButtons>
          <div>
            <AnimationTypeSelectorButtonGroup
              size={size}
              onClick={e =>
                handleButtonClick({ anchor: e.currentTarget, secondary: false, primary: true, tertiary: false })
              }
              label="Type"
              id={`Type-AnimationTypeSelectorButtonGroup-${panelId}`}
              disabled={disabled}
            >
              <Icon />
              <AnimationTypeSelectorButtonGroupText>{animationTypeTitle}</AnimationTypeSelectorButtonGroupText>
            </AnimationTypeSelectorButtonGroup>

            <div className="AnimationStageConfigurationOptions__subset">
              {((animationType !== NO_ANIMATION_KEY && !animationTypeHasNoDuration(animationType) && showDuration) ||
                stageKey === EMPHASIS_STAGE_KEY) && (
                <EditPanelNumberInput
                  id={`${panelId.toLowerCase().replace(' ', '-')}-duration`}
                  value={duration}
                  onChange={onDurationChange}
                  units="s"
                  label="Duration:"
                  disabled={disabled}
                />
              )}
              {!SecondaryChoiceButton && animationType !== NO_ANIMATION_KEY && showLoops && (
                <EditPanelNumberInput
                  id={`${panelId.toLowerCase().replace(' ', '-')}-primary-loops`}
                  value={loops}
                  onChange={onLoopsChange}
                  units="x"
                  label="Loops:"
                  min={1}
                  step={1}
                  disabled={disabled}
                />
              )}
            </div>
          </div>

          <div>
            {SecondaryChoiceButton && (
              <AnimationTypeSelectorButtonGroup
                size={size}
                onClick={e =>
                  handleButtonClick({ anchor: e.currentTarget, tertiary: false, secondary: true, primary: false })
                }
                label={secondaryMenu.choiceButtonLabel}
                id={`${secondaryMenu.choiceButtonLabel}-AnimationTypeSelectorButtonGroup-${panelId}`}
                disabled={disabled}
              >
                <SecondaryChoiceButton />
              </AnimationTypeSelectorButtonGroup>
            )}
            <div className="AnimationStageConfigurationOptions__subset">
              {SecondaryChoiceButton && animationType !== NO_ANIMATION_KEY && showLoops && (
                <EditPanelNumberInput
                  id={`${panelId.toLowerCase().replace(' ', '-')}-secondary-loops`}
                  value={loops}
                  onChange={onLoopsChange}
                  units="x"
                  label="Loops:"
                  min={1}
                  step={1}
                  disabled={disabled}
                />
              )}
            </div>
          </div>

          <div>
            {TertiaryChoiceButton && (
              <AnimationTypeSelectorButtonGroup
                size={size}
                onClick={e =>
                  handleButtonClick({ anchor: e.currentTarget, tertiary: true, secondary: false, primary: false })
                }
                label={tertiaryMenu.choiceButtonLabel}
                id={`${tertiaryMenu.choiceButtonLabel}-AnimationTypeSelectorButtonGroup-${panelId}`}
                disabled={disabled}
              >
                <TertiaryChoiceButton />
              </AnimationTypeSelectorButtonGroup>
            )}
            <div className="AnimationStageConfigurationOptions__subset">
              {TertiaryChoiceButton && animationType !== NO_ANIMATION_KEY && showLoops && (
                <EditPanelNumberInput
                  id={`${panelId.toLowerCase().replace(' ', '-')}-tertiary-loops`}
                  value={loops}
                  onChange={onLoopsChange}
                  units="x"
                  label="Loops:"
                  min={1}
                  disabled={disabled}
                />
              )}
            </div>
          </div>
        </AnimationTypeSelectorButtons>
      </div>

      <Popover
        className={popoverClassName}
        id={`${panelId}-animation-options-menu`}
        anchorEl={menuOpen?.anchor}
        keepMounted
        getContentAnchorEl={null}
        open={Boolean(menuOpen?.anchor)}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        PaperProps={{
          style: {
            maxHeight: '50vh',
            minWidth: 200
          }
        }}
      >
        {menuOpen?.primary && (
          <AnimationOptionMenu title="Type" variant={variant}>
            <AnimationStagePanelOptionsGrid>
              {animationTypeOptions.map(opt => {
                const { Icon: OptIcon, title: optTitle, type: optType } = opt;
                return (
                  <OptionMenuButton
                    selected={optType === animationType}
                    key={optType}
                    onClick={() => handleMenuItemClick(opt)}
                  >
                    <OptionMenuButtonIconAndText icon={<OptIcon flipImage={REVERSE_ICON_LIST.includes(optType)} />}>
                      {optTitle}
                    </OptionMenuButtonIconAndText>
                  </OptionMenuButton>
                );
              })}
            </AnimationStagePanelOptionsGrid>
          </AnimationOptionMenu>
        )}

        {menuOpen?.secondary && secondaryMenu && (
          <AnimationOptionMenu title={secondaryMenu.title} variant={variant}>
            <secondaryMenu.Component />
          </AnimationOptionMenu>
        )}
        {menuOpen?.tertiary && tertiaryMenu && (
          <AnimationOptionMenu title={tertiaryMenu.title} variant={variant}>
            <tertiaryMenu.Component />
          </AnimationOptionMenu>
        )}
      </Popover>
    </>
  );
};

export default AnimationStageConfigurationOptions;
